import { createContext, useState, useEffect, useCallback } from 'react';
import { hasPermission } from '../utils/permissions';

const API_URL = process.env.APP_API_URL || 'https://www.sanatandharmmahasabha.com/api/admin';
const AUTH_API_URL = process.env.APP_AUTH_URL || 'https://www.sanatandharmmahasabha.com';

export const AuthContext = createContext();

const readStored = (key) => sessionStorage.getItem(key) || localStorage.getItem(key);

// Keep the user object in whichever storage the token lives in.
const persistUser = (userData) => {
  const json = JSON.stringify(userData);
  if (sessionStorage.getItem('token')) sessionStorage.setItem('user', json);
  if (localStorage.getItem('token')) localStorage.setItem('user', json);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(readStored('token'));

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    localStorage.removeItem('rememberMe');
    localStorage.removeItem('user');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
  }, []);

  // Fetches the current account incl. role name and permissions.
  // Returns true when the session is valid.
  const refreshSession = useCallback(async (authToken = readStored('token')) => {
    if (!authToken) return false;
    try {
      const res = await fetch(`${API_URL}/me`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.status === 401 || res.status === 403) {
        logout();
        return false;
      }
      if (!res.ok) return true; // Server hiccup: keep the cached session.
      const data = await res.json();
      setUser(data);
      persistUser(data);
      return true;
    } catch (error) {
      console.error('Session refresh failed:', error);
      return true; // Offline: keep the cached session.
    }
  }, [logout]);

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = readStored('token');
      const storedUser = readStored('user');

      if (storedToken) {
        if (storedUser) {
          try {
            setUser(JSON.parse(storedUser));
          } catch (_e) {
            // ignore, refreshed below
          }
        }
        setToken(storedToken);
        // Always reload permissions: an admin may have changed this person's role.
        await refreshSession(storedToken);
      }
      setLoading(false);
    };

    initializeAuth();
  }, [refreshSession]);

  const login = async (credentials, rememberMe = false) => {
    try {
      const res = await fetch(`${AUTH_API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });

      const data = await res.json();

      if (res.ok && data.admin) {
        setUser(data.admin);
        setToken(data.token);

        if (rememberMe) {
          localStorage.setItem('token', data.token);
          localStorage.setItem('rememberMe', 'true');
          localStorage.setItem('user', JSON.stringify(data.admin));
        } else {
          localStorage.removeItem('rememberMe');
          localStorage.removeItem('user');
          sessionStorage.setItem('token', data.token);
          sessionStorage.setItem('user', JSON.stringify(data.admin));
        }

        return { success: true, admin: data.admin, token: data.token };
      }
      return { success: false, message: data.message || 'Login failed' };
    } catch (error) {
      console.error("Login error:", error);
      return { success: false, message: 'Network error. Please try again.' };
    }
  };

  const can = useCallback((permission) => hasPermission(user, permission), [user]);

  const getToken = () => token || readStored('token');

  return (
    <AuthContext.Provider value={{
      user,
      token: getToken(),
      login,
      logout,
      can,
      refreshSession,
      verifyToken: refreshSession,
      loading
    }}>
      {children}
    </AuthContext.Provider>
  );
};
